import { useEffect } from 'react';
import felloraIcon from '../../control/public/fellora-app-icon.svg?url';
import { Brand } from '../../control/src/components/ui/Brand';
import { FelloraMark } from '../../control/src/components/ui/FelloraMark';
import { Icon } from '../../control/src/components/ui/Icon';
import { productByKey, products, type Product, type ProductKey } from './content';

const signupUrl = 'https://app.fellora.app/signup';
const signInUrl = 'https://app.fellora.app/sign-in';
const pricingUrl = 'https://app.fellora.app/pricing#pricing';

function routeKey(): ProductKey | 'home' | 'pricing' | 'not-found' {
  const normalized = window.location.pathname.replace(/\/+$/, '') || '/';
  if (normalized === '/') return 'home';
  if (normalized === '/pricing') return 'pricing';
  const product = normalized.slice(1) as ProductKey;
  return productByKey.has(product) ? product : 'not-found';
}

function Header() {
  return (
    <header className="site-header">
      <a aria-label="Fellora home" className="site-brand" href="/">
        <Brand />
      </a>
      <nav aria-label="Main navigation" className="site-nav">
        <a href="/#products">Products</a>
        <a href="/sites-apps">Apps</a>
        <a href="/#integrations">Integrations</a>
        <a href={pricingUrl}>Pricing</a>
      </nav>
      <div className="header-actions">
        <a className="text-link header-login" href={signInUrl}>Log in</a>
        <a className="button button--small" href={signupUrl}>Start free</a>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div>
        <a aria-label="Fellora home" className="footer-brand" href="/"><Brand /></a>
        <p>Church events, people, and communication.</p>
      </div>
      <nav aria-label="Product links">
        <strong>Products</strong>
        {products.slice(0, 4).map((product) => <a href={`/${product.key}`} key={product.key}>{product.shortName}</a>)}
      </nav>
      <nav aria-label="More product links">
        <strong>More in Fellora</strong>
        {products.slice(4).map((product) => <a href={`/${product.key}`} key={product.key}>{product.shortName}</a>)}
      </nav>
      <nav aria-label="Company links">
        <strong>Fellora</strong>
        <a href={pricingUrl}>Pricing</a>
        <a href={signInUrl}>Sign in</a>
        <a href="https://fellora.app/privacy">Privacy</a>
        <a href="https://fellora.app/terms">Terms</a>
      </nav>
      <small>© 2026 Fellora. Church events, people, and communication.</small>
    </footer>
  );
}

function ProductFrame({
  alt,
  eager = false,
  image,
  label = 'Current Fellora App · Grace Community Church example data',
  variant = 'desktop',
}: {
  alt: string;
  eager?: boolean;
  image: string;
  label?: string;
  variant?: 'desktop' | 'public';
}) {
  return (
    <figure className={`product-frame product-frame--${variant}`}>
      <figcaption>
        <span><i /><i /><i /></span>
        <small>{label}</small>
      </figcaption>
      <img alt={alt} loading={eager ? 'eager' : 'lazy'} src={image} />
    </figure>
  );
}

const previewNavigation = [
  { icon: 'home' as const, label: 'Home' },
  { icon: 'people' as const, label: 'People' },
  { icon: 'calendar' as const, label: 'Calendar' },
  { icon: 'trend' as const, label: 'Attendance' },
  { icon: 'calendar' as const, label: 'Events' },
  { icon: 'pie' as const, label: 'Care' },
  { icon: 'message' as const, label: 'Messages' },
];

function HeroAppPreview() {
  return (
    <figure aria-label="Fellora App example dashboard" className="product-frame hero-app-preview">
      <figcaption>
        <span><i /><i /><i /></span>
        <small>Fellora App shown with example church data</small>
      </figcaption>
      <div className="hero-app-preview__window">
        <aside aria-label="Example Fellora navigation" className="hero-app-preview__sidebar">
          <div className="hero-app-preview__brand"><FelloraMark /><strong>Fellora</strong></div>
          <nav>
            {previewNavigation.map((item) => (
              <span className={item.label === 'Home' ? 'is-active' : undefined} key={item.label}>
                <Icon name={item.icon} size={14} />{item.label}
              </span>
            ))}
          </nav>
        </aside>
        <section className="hero-app-preview__content">
          <header>
            <span><i /> Connected</span>
            <span>Grace Community Church</span>
          </header>
          <div className="hero-app-preview__heading">
            <small>THURSDAY, AUGUST 6</small>
            <h2>Good morning, Elise.</h2>
            <p>Here’s what’s happening at your church.</p>
          </div>
          <div className="hero-app-preview__summary">
            <article>
              <span><Icon name="calendar" size={15} /> Upcoming events</span>
              <strong>Sunday Gathering</strong>
              <small>Sunday · 10:00 AM</small>
            </article>
            <article>
              <span><Icon name="people" size={15} /> People</span>
              <strong>248 people</strong>
              <small>12 households updated this month</small>
            </article>
            <article>
              <span><Icon name="pie" size={15} /> Care</span>
              <strong>2 active meal plans</strong>
              <small>One delivery is due today</small>
            </article>
          </div>
          <div className="hero-app-preview__next">
            <div>
              <small>NEXT EVENT</small>
              <strong>Sunday Gathering</strong>
              <span>Worship Center · 10:00 AM</span>
            </div>
            <span className="hero-app-preview__status"><Icon name="check" size={13} /> Ready to publish</span>
          </div>
        </section>
      </div>
    </figure>
  );
}

function ProductRail({ active }: { active?: ProductKey }) {
  return (
    <nav aria-label="Fellora products" className="product-rail">
      {products.map((product) => (
        <a aria-current={active === product.key ? 'page' : undefined} href={`/${product.key}`} key={product.key}>
          <Icon name={product.icon} size={17} />
          {product.shortName}
        </a>
      ))}
    </nav>
  );
}

type IntegrationItem = { label: string; icon?: string; iconAlt?: string };

const integrationGroups: Array<{ title: string; items: IntegrationItem[] }> = [
  {
    title: 'Publish and notify',
    items: [
      { label: 'Church website' },
      { label: 'Public event pages' },
      { label: 'Church app' },
      { label: 'App notifications' },
      { label: 'Email' },
      { label: 'Text messaging' },
    ],
  },
  {
    title: 'Share church calendars',
    items: [
      { label: 'Google Calendar', icon: '/integration-icons/google-calendar.png', iconAlt: 'Google Calendar' },
      { label: 'Apple Calendar', icon: '/integration-icons/apple-calendar.png', iconAlt: 'Apple Calendar' },
      { label: 'Outlook', icon: '/integration-icons/outlook.png', iconAlt: 'Microsoft Outlook' },
    ],
  },
];

function IntegrationChip({ icon, iconAlt, label }: IntegrationItem) {
  return (
    <span className={`integration-chip${icon ? ' integration-chip--app' : ''}`}>
      {icon ? <img alt={iconAlt ?? ''} src={icon} /> : null}
      <span>{label}</span>
    </span>
  );
}

function CtaBand() {
  return (
    <section className="cta-band">
      <FelloraMark className="cta-band__mark" />
      <div>
        <small>Start free</small>
        <h2>Set up Fellora for your church.</h2>
        <p>Begin with People, Calendar, and Attendance. Upgrade when your church needs more.</p>
      </div>
      <a className="button button--light" href={signupUrl}>Start Fellora Free <span aria-hidden="true">→</span></a>
    </section>
  );
}

function HomePage() {
  return (
    <>
      <main>
        <section className="hero">
          <img alt="" aria-hidden="true" className="hero__wash" src={felloraIcon} />
          <div className="hero__copy">
            <span className="eyebrow">Church software for events, people, and ministry</span>
            <h1>Plan events. Keep your church informed.</h1>
            <p>
              Manage events, calendars, people, volunteers, care, messages, your website,
              and your church app without entering the same details again.
            </p>
            <div className="hero__actions">
              <a className="button" href={signupUrl}>Start Fellora Free <span aria-hidden="true">→</span></a>
              <a className="button button--quiet" href="#products">See what’s included</a>
            </div>
            <div className="hero__promise">
              <span><Icon name="check" size={15} />Useful free plan</span>
              <span><Icon name="check" size={15} />No card required</span>
              <span><Icon name="check" size={15} />Review AI drafts before publishing</span>
            </div>
          </div>
          <div className="hero__product">
            <HeroAppPreview />
            <span className="float-note float-note--top"><Icon name="sparkles" size={17} />See what needs attention</span>
            <span className="float-note float-note--bottom"><Icon name="check" size={17} />Start with an event</span>
          </div>
        </section>

        <section className="suite-intro" id="products">
          <div className="section-heading">
            <span className="eyebrow">What Fellora includes</span>
            <h2>Manage the work your church does every week.</h2>
            <p>
              People, calendars, attendance, events, volunteers, care, messages, the website, and the church app
              use the same saved church information.
            </p>
          </div>
          <ProductRail />
          <div className="product-grid">
            {products.map((product, index) => (
              <a className={`product-card product-card--${(index % 4) + 1}`} href={`/${product.key}`} key={product.key}>
                <span className="product-card__icon"><Icon name={product.icon} size={24} /></span>
                <small>Included with Fellora {product.minimumPlan}</small>
                <h3>{product.name}</h3>
                <p>{product.summary}</p>
                <strong>Explore {product.shortName} <span aria-hidden="true">→</span></strong>
              </a>
            ))}
          </div>
        </section>

        <section className="story story--events">
          <div className="story__copy">
            <span className="eyebrow">Events + calendars</span>
            <h2>Enter the details once. Use them wherever you share the event.</h2>
            <p>
              Public events, private events, and meetings use one schedule. Update the event,
              then publish the current details to your website, calendars, and messages.
            </p>
            <ul className="friendly-list">
              <li><Icon name="check" size={16} />Public and private visibility</li>
              <li><Icon name="check" size={16} />Google, Apple, and Outlook calendar feeds</li>
              <li><Icon name="check" size={16} />Registration with the church’s payment link</li>
            </ul>
            <a className="inline-link" href="/events">See Events &amp; Meetings <span aria-hidden="true">→</span></a>
          </div>
          <ProductFrame
            alt="Fellora calendar showing church events and private meal schedules"
            image="/screenshots/fellora-calendar.jpg"
          />
        </section>

        <section className="story story--reverse story--attendance" id="attendance-preview">
          <div className="story__copy">
            <span className="eyebrow">Attendance + church metrics</span>
            <h2>See the whole church—or one service, Area, or ministry.</h2>
            <p>
              Record the numbers your church follows, compare services and Areas, and review
              long-term patterns without building another spreadsheet.
            </p>
            <ul className="friendly-list">
              <li><Icon name="check" size={16} />Multiple services in the same Area</li>
              <li><Icon name="check" size={16} />Custom ministry measurements and notes</li>
              <li><Icon name="check" size={16} />Church-wide and service-level trends</li>
            </ul>
            <a className="inline-link" href="/attendance">See Attendance <span aria-hidden="true">→</span></a>
          </div>
          <ProductFrame
            alt="Fellora Attendance showing church-wide trends, service filters, Areas, and recent records"
            image="/screenshots/fellora-attendance.jpg"
          />
        </section>

        <section className="story story--people" id="people-preview">
          <div className="story__copy">
            <span className="eyebrow">People + volunteers + care</span>
            <h2>Keep the directory, Groups, Teams, volunteers, and care up to date.</h2>
            <p>
              Record each person’s church involvement separately. Belonging to a Group,
              volunteering, or receiving care never signs someone up for messages.
            </p>
            <div className="mini-links">
              <a href="/people"><Icon name="people" size={18} />People, Groups &amp; Teams</a>
              <a href="/volunteers"><Icon name="hand" size={18} />Volunteers</a>
              <a href="/care"><Icon name="pie" size={18} />Care</a>
            </div>
          </div>
          <ProductFrame
            alt="Fellora People showing a searchable directory, households, and church app access"
            image="/screenshots/fellora-people.jpg"
          />
        </section>

        <section className="app-pair">
          <div className="section-heading section-heading--center">
            <span className="eyebrow">Two apps for different people</span>
            <h2>Staff use Fellora. Members and volunteers use your church app.</h2>
          </div>
          <div className="app-pair__grid">
            <article>
              <span className="app-icon app-icon--fellora"><FelloraMark /></span>
              <small>For church staff</small>
              <h3>Fellora App</h3>
              <p>Events, people, communication, volunteers, care, websites, settings, and support.</p>
              <a href="/sites-apps">See the staff experience <span aria-hidden="true">→</span></a>
            </article>
            <article>
              <span className="app-icon app-icon--church"><Icon name="people" size={31} /></span>
              <small>For members &amp; volunteers</small>
              <h3>Church app</h3>
              <p>Events, serving, messages, profile choices, personal calendar, and church notifications.</p>
              <a href="/sites-apps">See the church app <span aria-hidden="true">→</span></a>
            </article>
            <ProductFrame
              alt="Fellora Designer showing the church website editor and live preview"
              image="/screenshots/fellora-designer.jpg"
            />
          </div>
        </section>

        <section className="story story--reverse story--messages" id="messages-preview">
          <div className="story__copy">
            <span className="eyebrow">Email + text + app notifications</span>
            <h2>Start with what you need to say—not a list of old drafts.</h2>
            <p>
              Choose the audience and channels, start from a template, write it yourself,
              or ask Fellora AI for help. Review every channel before you schedule the campaign.
            </p>
            <div className="mini-links">
              <a href="/communication"><Icon name="message" size={18} />Guided campaign builder</a>
              <a href="/communication"><Icon name="sparkles" size={18} />AI help or write it yourself</a>
              <a href="/communication"><Icon name="calendar" size={18} />Campaigns and welcome Stories</a>
            </div>
          </div>
          <ProductFrame
            alt="Fellora Messages showing the guided campaign builder, templates, recent campaigns, and Stories"
            image="/screenshots/fellora-messages.jpg"
          />
        </section>

        <section className="integrations" id="integrations">
          <div className="integrations__copy">
            <span className="eyebrow">Publishing and communication</span>
            <h2>Use the channels churches rely on every week.</h2>
            <p>
              Publish to your website and church app, send email, text, and app notifications,
              and share calendars with Apple, Google, or Outlook.
            </p>
            <div className="integration-groups">
              {integrationGroups.map((group) => <div key={group.title}>
                <strong>{group.title}</strong>
                {group.items.map((item) => <IntegrationChip key={item.label} {...item} />)}
              </div>)}
            </div>
          </div>
          <ProductFrame
            alt="Fellora Channels showing email, text, website, and calendar setup"
            image="/screenshots/fellora-integrations.jpg"
          />
        </section>

        <section className="assist-peek">
          <FelloraMark className="assist-peek__mark" />
          <div>
            <span className="eyebrow">Fellora Assist</span>
            <h2>Get a first draft. Review it before anything is published.</h2>
            <p>
              Draft a complete event kit, ask for a revision, approve only the pieces you
              want, and answer support questions from published manager guides.
            </p>
            <a className="button button--quiet" href="/assist">Meet Fellora Assist</a>
          </div>
          <ProductFrame
            alt="Fellora AI event planner with editable prompts and approval controls"
            image="/screenshots/fellora-assist.jpg"
          />
        </section>

        <CtaBand />
      </main>
    </>
  );
}

function ProductPage({ product }: { product: Product }) {
  return (
    <main>
      <ProductRail active={product.key} />
      <section className="product-hero">
        <img alt="" aria-hidden="true" className="product-hero__wash" src={felloraIcon} />
        <div>
          <span className="eyebrow">{product.eyebrow}</span>
          <h1>{product.headline}</h1>
          <p>{product.summary}</p>
          <div className="hero__actions">
            <a className="button" href={signupUrl}>Start Fellora Free <span aria-hidden="true">→</span></a>
            <a className="button button--quiet" href={pricingUrl}>See everything included</a>
          </div>
        </div>
        <ProductFrame alt={product.screenshotAlt} eager image={product.screenshot} />
      </section>

      <section className="feature-trio">
        {product.features.map((feature, index) => (
          <article key={feature.title}>
            <span>0{index + 1}</span>
            <h2>{feature.title}</h2>
            <p>{feature.copy}</p>
          </article>
        ))}
      </section>

      <section className="product-story">
        <ProductFrame
          alt={product.secondaryAlt}
          image={product.secondaryScreenshot}
          label={product.key === 'sites-apps' || product.key === 'events'
            ? 'Current public Fellora experience · Grace Community Church example data'
            : 'Current Fellora App · Grace Community Church example data'}
          variant={product.key === 'sites-apps' || product.key === 'events' ? 'public' : 'desktop'}
        />
        <div>
          <span className="eyebrow">{product.secondaryEyebrow}</span>
          <h2>{product.secondaryHeadline}</h2>
          <p>{product.secondaryCopy}</p>
          <a className="inline-link" href="/#products">Explore the whole Fellora suite <span aria-hidden="true">→</span></a>
        </div>
      </section>

      {product.key === 'sites-apps' ? <AppsExplainer /> : null}
      {product.key === 'communication' ? <ChannelReadiness /> : null}
      {product.key === 'assist' ? <AiTrust /> : null}
      <CtaBand />
    </main>
  );
}

function AppsExplainer() {
  return (
    <section className="detail-band">
      <div>
        <span className="app-icon app-icon--fellora"><FelloraMark /></span>
        <small>Fellora App</small>
        <h2>Manage your church from a browser or installed app.</h2>
        <p>Staff can use the same church account on desktop, tablet, or phone.</p>
      </div>
      <div>
        <span className="app-icon app-icon--church"><Icon name="people" size={31} /></span>
        <small>Church app</small>
        <h2>A church app for members and volunteers.</h2>
        <p>People can find events, see serving assignments, read messages, and choose notifications.</p>
      </div>
      <div>
        <span className="app-icon app-icon--lilac"><Icon name="website" size={31} /></span>
        <small>Church website</small>
        <h2>Publish your welcome, service times, and events.</h2>
        <p>Publish the welcome, service details, contact information, events, and church app link.</p>
      </div>
    </section>
  );
}

function ChannelReadiness() {
  return (
    <section className="status-explainer">
      <div className="section-heading">
        <span className="eyebrow">Connection status</span>
        <h2>See what works now and what still needs setup.</h2>
      </div>
      <div>
        <article><span className="status-dot status-dot--ready" /><strong>Ready</strong><p>Built in or connected and available to use.</p></article>
        <article><span className="status-dot status-dot--guided" /><strong>Needs setup</strong><p>Fellora shows what your church must connect first.</p></article>
        <article><span className="status-dot status-dot--preview" /><strong>Provider review</strong><p>Text messaging can show that Fellora, the church, or the provider has the next step.</p></article>
      </div>
    </section>
  );
}

function AiTrust() {
  return (
    <section className="ai-trust">
      <div className="section-heading">
        <span className="eyebrow">AI drafts require review</span>
        <h2>How Fellora Assist works.</h2>
      </div>
      <div>
        <article><Icon name="edit" size={22} /><strong>Editable</strong><p>AI work is a starting point, not a locked answer.</p></article>
        <article><Icon name="check" size={22} /><strong>Approval required</strong><p>Drafts and proposed guides wait for an authorized person.</p></article>
        <article><Icon name="lock" size={22} /><strong>Published guides only</strong><p>Church-facing help uses published manager guides, not private Fellora procedures.</p></article>
      </div>
    </section>
  );
}

function NotFoundPage() {
  return (
    <main className="not-found">
      <FelloraMark />
      <span className="eyebrow">Page not found</span>
      <h1>That Fellora page isn’t here.</h1>
      <p>Go back to the homepage to see Fellora.</p>
      <a className="button" href="/">Return home</a>
    </main>
  );
}

export function App() {
  const route = routeKey();
  const product = route !== 'home' && route !== 'pricing' && route !== 'not-found'
    ? productByKey.get(route)
    : undefined;

  useEffect(() => {
    if (route === 'pricing') {
      window.location.replace(pricingUrl);
      return;
    }
    const title = route === 'home'
      ? 'Fellora · Church events, people, messages, and more'
      : product
        ? `${product.name} · Fellora`
        : 'Page not found · Fellora';
    const description = route === 'home'
      ? 'Manage church events, calendars, people, volunteers, care, messages, your website, and your church app with Fellora.'
      : product?.summary ?? 'Explore Fellora church software.';
    const canonicalPath = route === 'not-found' ? '/' : window.location.pathname;
    const canonicalUrl = new URL(canonicalPath, 'https://fellora.app').href;
    const robots = route === 'not-found' || window.location.hostname === 'web.fellora.app'
      ? 'noindex,follow'
      : 'index,follow';
    const setMeta = (selector: string, attribute: 'content', value: string) => {
      document.querySelector<HTMLMetaElement>(selector)?.setAttribute(attribute, value);
    };

    document.title = title;
    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[name="robots"]', 'content', robots);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', canonicalUrl);
    document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.setAttribute('href', canonicalUrl);
    window.scrollTo(0, 0);
  }, [product, route]);

  useEffect(() => {
    if (route !== 'home') return;
    const query = new URLSearchParams(window.location.search);
    if (!query.has('verify') && !query.has('signin')) return;
    window.location.replace(`${signupUrl}${window.location.search}`);
  }, [route]);

  return (
    <div className="marketing-site">
      <Header />
      {route === 'home' ? <HomePage /> : null}
      {route === 'pricing' ? <main className="not-found" aria-busy="true"><p>Opening current Fellora pricing…</p></main> : null}
      {product ? <ProductPage product={product} /> : null}
      {route === 'not-found' ? <NotFoundPage /> : null}
      <Footer />
    </div>
  );
}
